import React, { useCallback, useEffect, useState, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import clsx from 'clsx';
import i18n from 'i18n-js';
import moment from 'moment';
import { UserContext } from 'context';
import { withRouter } from 'react-router-dom';
import ItemStatus from 'components/ItemStatus';
import { InvoiceModal } from 'components/Modals';
import { Card, CollapseCard } from 'components/UI';
import { formatAmount, getDynamicLink } from 'utils';
import { InvoiceActions } from 'state/modules/invoice';
import { useStyles, listTextStyles, collapseCardStyles } from './Invoice.styled';
import { Box, List, Grid, ListItem, Typography, ListItemText, Divider } from '@material-ui/core';

const Invoice = ({ id, title, isStatementEntry }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const listClasses = listTextStyles();
  const collapseCardClasses = collapseCardStyles();
  const [openEditModal, setOpenEditModal] = useState(false);
  const { localeFormat } = useSelector((state) => state.organizations);
  const { current, fetchedProcessInvoice, invoice } = useSelector((state) => state.invoice);

  const isInvoiceStatement = invoice?.receivable_type === 'statement';

  const sharingType = useSelector(
    (state) => state.config?.settings?.core?.partnership?.sharing_type
  );
  const core = useSelector((state) => state.config?.settings?.core || {});
  const hasPartnership = core?.partnership?.enabled || false;
  const isFFBEnabled = sharingType === 'FFB' && hasPartnership;
  const { orgType } = useContext(UserContext);

  /**
   * Get invoice by id
   */
  const fetchInvoiceById = useCallback(
    (id) => {
      dispatch(InvoiceActions.fetchInvoiceById(id));
    },
    [dispatch]
  );

  useEffect(() => {
    if (id) {
      fetchInvoiceById(id);
    }
  }, [fetchInvoiceById, id]);

  useEffect(() => {
    if (fetchedProcessInvoice) {
      fetchInvoiceById(id);
    }
  }, [fetchInvoiceById, id, fetchedProcessInvoice]);

  const getItemString = (type, id) => {
    switch (type) {
      case 'ReservationDriver':
        return `${i18n.t('instance.reservation')} ID#${id}`;
      case 'Vehicle':
        return `${i18n.t('instance.vehicle')} ID#${id}`;
      case 'Driver':
        return `${i18n.t('instance.driver')} ID#${id}`;
      case 'Invoice':
        return `${i18n.t('instance.invoice')} ID#${id}`;
      case 'SubscriptionPeriod':
        return `${i18n.t('instance.subscription_period')} ID#${id}`;
      case 'Subscription':
        return `${i18n.t('instance.subscription')} ID#${id}`;
      case 'OwnerVehicleGroup':
        return 'Fleet';
      default:
        return id;
    }
  };

  /**
   * Parse invoice data
   * @param {object} invoice Object with invoice data
   */
  const getInvoiceData = (invoice = {}) => {
    const { source, source_id } =
      invoice?.source_name === 'SubscriptionPeriod'
        ? {
            source: invoice?.extra_source_name,
            source_id: invoice?.extra_source_id,
          }
        : { source: invoice?.source_name, source_id: invoice?.source_id };

    const invoiceInfo = [
      {
        title: i18n.t(
          isStatementEntry ? 'statement_entries.information' : 'invoice.invoice_information'
        ),
        items: [
          {
            label: `${i18n.t('invoice.from')}:`,
            value: invoice?.account_emitter?.customer_name || '-',
            dataCy: 'from',
          },
          {
            label: `${i18n.t('invoice.to')}:`,
            value: getDynamicLink(
              `${invoice?.account_customer?.customer_name} ID#${invoice?.account_customer?.holder_id}`,
              invoice?.account_customer?.holder_id,
              invoice?.account_customer?.holder_type === 'User' ? 'Driver' : 'Organization'
            ),
            dataCy: 'to',
            classes: 'link',
          },
          {
            label: `${i18n.t('invoice.source')}:`,
            value: getDynamicLink(
              getItemString(source, source_id),
              source_id,
              source,
              undefined,
              undefined,
              invoice?.description
            ),
            dataCy: 'source',
            classes: 'link',
          },
          {
            label: isInvoiceStatement
              ? `${i18n.t('invoice.due_date')}:`
              : `${i18n.t('invoice.due_datetime')}:`,
            value: moment(invoice?.due_time).isValid()
              ? isInvoiceStatement
                ? moment(invoice?.due_time).utc(false).format(localeFormat)
                : moment(invoice?.due_time).format(`${localeFormat}  hh:mm A`)
              : '-',
            dataCy: 'due_time',
          },
          {
            label: `${i18n.t('invoice.processed_datetime')}:`,
            value: invoice?.processed_time
              ? moment(invoice?.processed_time).format(`${localeFormat} h:mm A`)
              : '-',
            dataCy: 'due_time',
            hide: !invoice?.processed_time,
          },
        ],
      },
      ...(isStatementEntry
        ? []
        : [
            {
              title: 'Payment',
              subtitle:
                `Paid: $${formatAmount(getTotalPaid(invoice?.paid_card, invoice?.paid_credit))}` ||
                '-',
              items: [
                {
                  label: `${i18n.t('invoice.total_invoiced')}:`,
                  value: `$${formatAmount(invoice?.invoiced || 0)}` || '-',
                  dataCy: 'total_invoiced',
                },
                {
                  label: `${i18n.t('invoice.total_paid')}:`,
                  value:
                    `$${formatAmount(getTotalPaid(invoice?.paid_card, invoice?.paid_credit))}` ||
                    '-',
                  dataCy: 'total_paid',
                },
                {
                  label: `${i18n.t('invoice.amount_due')}:`,
                  value:
                    `$${formatAmount(
                      (invoice?.invoiced || 0) -
                        (invoice?.paid_card || 0) -
                        (invoice?.paid_credit || 0)
                    )}` || '-',
                  dataCy: 'amount_due',
                },
              ],
            },
          ]),
    ];

    return invoiceInfo;
  };

  const getTotalPaid = (...payments) => payments.reduce((p, c) => p + (c || 0), 0);

  const invoiceTitle = (
    <Typography color="textPrimary" component="span">
      <Box fontSize={14} fontWeight="fontWeightMedium" data-cy="invoice-title">
        {title || isStatementEntry
          ? i18n.t('statement_entries.widget_title')
          : i18n.t('common.Invoice')}
      </Box>
    </Typography>
  );

  const ListTitleComponent = ({ title, subtitle }) => {
    return (
      <Box className={classes.titleRow}>
        <Typography component="span" className={classes.title}>
          {title}
        </Typography>
        <Typography component="span" className={classes.subtitle}>
          {subtitle}
        </Typography>
      </Box>
    );
  };

  const handleOpenEditModal = () => {
    setOpenEditModal((prevState) => !prevState);
  };

  /**
   * Vertical menu list
   * It has the label and the function for each action
   */
  const items = [
    {
      title: i18n.t('invoice.edit_invoice'),
      onClick: handleOpenEditModal,
      display: current?.[id]?.is_draft,
      can: { name: 'invoice', action: 'create' },
    },
  ];

  return (
    <Card
      menuItems={items}
      title={invoiceTitle}
      headerActionType="vertical"
      data-cy="invoice-detail-widget"
    >
      <Box className={classes.cardContent}>
        <Grid container spacing={1} direction="row" alignItems="center">
          <Grid item xs={7}>
            <Typography variant="h3">
              <Box data-cy="invoice-id" className={classes.name}>
                {getDynamicLink(
                  `ID# ${id}`,
                  id,
                  orgType === 'msServiceOrg' && isFFBEnabled ? 'InvoicePreview' : 'Invoice'
                )}
              </Box>
            </Typography>
          </Grid>
          {current[id]?.status && (
            <Grid item xs={5} className={`${classes.textRight}`}>
              <ItemStatus i18nKey="invoice_statuses" status={current[id]?.status} />
            </Grid>
          )}
        </Grid>

        <Divider className={classes.divider} light={true} />

        <div>
          {getInvoiceData(current[id]).map((val, index) => (
            <CollapseCard
              key={index}
              title={
                val?.subtitle ? (
                  <ListTitleComponent title={val?.title} subtitle={val?.subtitle} />
                ) : (
                  val?.title
                )
              }
              customClasses={collapseCardClasses}
              dataCy="invoice-widget-card"
            >
              <List className={classes.list}>
                {val?.items?.map((val, index) =>
                  !val.hide ? (
                    <ListItem
                      key={index}
                      className={clsx(classes.listItem, val?.classes)}
                      data-cy={val.dataCy}
                    >
                      <ListItemText
                        classes={listClasses}
                        primary={val.label}
                        secondary={
                          <Box
                            display="flex"
                            component="span"
                            flexDirection="column"
                            className={classes.textRight}
                          >
                            <Typography
                              variant="body2"
                              component="span"
                              color="textSecondary"
                              className={classes?.[val?.classes]}
                            >
                              {val.value}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ) : null
                )}
              </List>
            </CollapseCard>
          ))}
        </div>
      </Box>
      {openEditModal && <InvoiceModal open={openEditModal} handleClick={handleOpenEditModal} />}
    </Card>
  );
};

export default withRouter(Invoice);
